
import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LeadScoreBreakdown as LeadScoreBreakdownType } from '@/types/leadScore';

interface LeadScoreBreakdownProps {
  scoreBreakdown: LeadScoreBreakdownType | null;
  isLoading: boolean;
}

const LeadScoreBreakdown: React.FC<LeadScoreBreakdownProps> = ({
  scoreBreakdown,
  isLoading
}) => {
  if (isLoading || !scoreBreakdown) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Score Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">Loading score details...</p>
        </CardContent>
      </Card>
    );
  }

  const { demographic, engagement, activity, recency } = scoreBreakdown.scoreDetails;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Score Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="demographic">
            <AccordionTrigger className="flex justify-between">
              <span>Demographic Factors</span>
              <span className="font-semibold">{demographic.score} pts</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {demographic.factors.map((factor, index) => (
                  <div key={index} className="flex justify-between items-center text-sm py-1 border-b">
                    <span>{factor.description}</span>
                    <Badge variant="outline">{factor.points} pts</Badge>
                  </div>
                ))}
                {demographic.factors.length === 0 && (
                  <p className="text-muted-foreground text-sm">No demographic factors recorded</p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="engagement">
            <AccordionTrigger className="flex justify-between">
              <span>Engagement Factors</span>
              <span className="font-semibold">{engagement.score} pts</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {engagement.factors.map((factor, index) => (
                  <div key={index} className="flex justify-between items-center text-sm py-1 border-b">
                    <span>{factor.description}</span>
                    <Badge variant="outline">{factor.points} pts</Badge>
                  </div>
                ))}
                {engagement.factors.length === 0 && (
                  <p className="text-muted-foreground text-sm">No engagement factors recorded</p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="activity">
            <AccordionTrigger className="flex justify-between">
              <span>Activity Factors</span>
              <span className="font-semibold">{activity.score} pts</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {activity.factors.map((factor, index) => (
                  <div key={index} className="flex justify-between items-center text-sm py-1 border-b">
                    <span>{factor.description}</span>
                    <Badge variant="outline">{factor.points} pts</Badge>
                  </div>
                ))}
                {activity.factors.length === 0 && (
                  <p className="text-muted-foreground text-sm">No activity factors recorded</p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="recency">
            <AccordionTrigger className="flex justify-between">
              <span>Recency Factors</span>
              <span className="font-semibold">{recency.score} pts</span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {recency.factors.map((factor, index) => (
                  <div key={index} className="flex justify-between items-center text-sm py-1 border-b">
                    <span>{factor.description}</span>
                    <Badge variant="outline">{factor.points} pts</Badge>
                  </div>
                ))}
                {recency.factors.length === 0 && (
                  <p className="text-muted-foreground text-sm">No recency factors recorded</p>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default LeadScoreBreakdown;
